// export default UserInformationList;
import React, { useEffect, useState } from 'react';
import { Card, ListGroup } from 'react-bootstrap';
import * as BI from 'react-bootstrap-icons';
import { Behance, Github, Globe, Linkedin } from 'react-bootstrap-icons';
import PortfolioUpdateFlow from '../UserProfile/PortfolioUpdateFlow';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import { normalizeUrl } from '../../utils/url';

export type UserInfoSection = 'personal' | 'work' | 'socials';

interface UserPropTypes {
  userData: any;
  onTriggerUpdateFlow?: () => void;
  refreshUser?: () => void | Promise<void>;
  sections?: UserInfoSection[];
}

const UserInformationList: React.FC<UserPropTypes> = ({
  userData,
  onTriggerUpdateFlow,
  refreshUser,
  sections = ['personal', 'work', 'socials'],
}) => {
  const showPersonal = sections.includes('personal');
  const showWork = sections.includes('work');
  const showSocials = sections.includes('socials');
  const sectionTitle =
    sections.length === 1 && sections[0] === 'work'
      ? 'Work Preferences'
      : sections.length === 1 && sections[0] === 'socials'
        ? 'Socials'
        : 'Profile Information';
  const [socials, setSocials] = React.useState(userData?.socials || {});
  const renderField = (
    Icon: React.ComponentType<any>,
    label: string,
    value?: string,
    link?: string,
    color?: string,
    iconTitle?: string
  ) => (
    <ListGroup.Item className="d-flex align-items-center py-2 info-item">
      <Icon className="me-3 text-success" size={20} title={iconTitle} />
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className={color}>
          {value}
        </a>
      ) : (
        <span>{value || label}</span>
      )}
    </ListGroup.Item>
  );

  useEffect(() => {
    setSocials(userData?.otherSocial || {});
  }, [userData?.otherSocial]);

  const [showModal, setShowModal] = useState(false);

  const location = [userData?.city, userData?.state, userData?.country].filter(Boolean).join(', ');

  return (
    <>
      <Card data-profile-focus="basics">
        <Card.Header className="preview-cards-ref-header">
          <span className="inter-style section-title" style={{ color: 'white' }}>
            {sectionTitle}
          </span>
          {showPersonal ? (
            <button
              title="Edit profile"
              type="button"
              className="user-info-edit-button"
              data-profile-focus="basics-edit"
              onClick={() => setShowModal(true)}
            >
              <EditIcon style={{ width: '15px', height: '15px' }} aria-hidden />
            </button>
          ) : null}
        </Card.Header>
        {/* Ensure full width */}
        <Card.Body className="inter-style">
          <ListGroup variant="flush">
            {showPersonal ? (
              <>
                {renderField(
                  BI.GenderAmbiguous,
                  'Gender',
                  userData?.gender,
                  undefined,
                  undefined,
                  'Gender'
                )}
                {renderField(BI.GeoAlt, 'Location', location, undefined, undefined, 'Location')}
                {renderField(
                  BI.Telephone,
                  'Phone Number',
                  userData?.phoneNumber
                    ? String(userData.phoneNumber).startsWith('+')
                      ? String(userData.phoneNumber)
                      : `+${userData.phoneNumber}`
                    : undefined,
                  userData?.phoneNumber &&
                    `tel:${String(userData.phoneNumber).startsWith('+') ? '' : '+'}${userData.phoneNumber}`,
                  'text-success',
                  'Telephone'
                )}
                {renderField(BI.Envelope, 'Email', userData?.email, undefined, undefined, 'E-mail')}
                {renderField(
                  BI.Calendar,
                  'Date of Birth',
                  userData?.dateOfBirth,
                  undefined,
                  undefined,
                  'Date of Birth'
                )}
              </>
            ) : null}

            {showWork ? (
              <>
                {!showPersonal ? null : (
                  <ListGroup.Item className="mt-3 mb-1 px-0">
                    <strong>Work Preferences</strong>
                  </ListGroup.Item>
                )}
                {renderField(
                  BI.Building,
                  'Current Company',
                  userData?.company,
                  undefined,
                  undefined,
                  'Current Company'
                )}
                {renderField(
                  BI.ArrowRepeat,
                  'Willing to Relocate',
                  userData?.willingToRelocate,
                  undefined,
                  undefined,
                  'Willing to Relocate'
                )}
                {renderField(
                  BI.HouseDoor,
                  'Preferred Work Arrangements',
                  userData?.workArrangements,
                  undefined,
                  undefined,
                  'Preferred Work Arrangements'
                )}
                {userData?.salary &&
                  userData?.salary !== 'Not indicated' &&
                  renderField(
                    BI.CashStack,
                    'Salary',
                    isNaN(Number(userData.salary))
                      ? userData.salary // e.g. "I prefer to negotiate"
                      : `$${Number(userData.salary).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`,
                    undefined,
                    undefined,
                    'Salary'
                  )}

                {renderField(
                  BI.Clock,
                  'Time Needed to Start',
                  userData?.timeToStart && `${userData?.timeToStart}`,
                  undefined,
                  undefined,
                  'Notice Period'
                )}
                {userData?.flexibleType &&
                  renderField(
                    BI.ArrowsMove,
                    'Flexibility',
                    userData?.flexibleType,
                    undefined,
                    undefined,
                    'Flexibility'
                  )}
              </>
            ) : null}
          </ListGroup>
          {showSocials ? (
            <ListGroup.Item className="info-section d-flex align-items-center">
              <div className="info-heading me-auto">Socials</div>
              <div className="social-icons d-flex gap-2">
                {userData?.linkedin && (
                  <a
                    href={normalizeUrl(userData?.linkedin)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#198754' }}
                  >
                    <Linkedin size={24} />
                  </a>
                )}
                {userData?.github && (
                  <a
                    href={normalizeUrl(userData?.github)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Github size={24} />
                  </a>
                )}
                {userData?.behance && (
                  <a
                    href={normalizeUrl(userData?.behance)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Behance size={24} />
                  </a>
                )}

                {userData?.otherSocial && (
                  <a
                    href={normalizeUrl(userData?.otherSocial)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#198754' }}
                  >
                    <Globe size={24} />
                  </a>
                )}
              </div>
            </ListGroup.Item>
          ) : null}
        </Card.Body>
      </Card>
      {showModal && showPersonal && (
        <PortfolioUpdateFlow
          show={showModal}
          onClose={() => setShowModal(false)}
          userData={userData}
          refreshUser={refreshUser ?? onTriggerUpdateFlow}
        />
      )}
      {socials && <div></div>}
    </>
  );
};

export default UserInformationList;
